import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeUpdateRequestDto } from '../../../store/landscapeAPI/theme/dto/request/theme-update-request.dto';
import { ThemeResponseDto } from '../../../store/landscapeAPI/theme/dto/response/theme-response.dto';
import {
  fetchThemeById,
  updateTheme,
} from '../../../store/landscapeAPI/theme/reducers/theme.slice';
import { AppDispatch, RootState } from '../../../store/store';
import Spinner from '../../atoms/Spinner/Spinner';
import { ReportingDetailsThemesEditTemplate } from '../../templates';
import { ImageMeta } from '../../../store/mediaAPI/media/dto/request/media-save-request';
import { MediaSaveResponseDto } from '../../../store/mediaAPI/media/dto/response/media-save-response';
import { saveMedia } from '../../../store/mediaAPI/media/reducers/media.slice';
import {
  getLanguageKey,
  LocalesLanguagesConstants,
} from '../../../locales/locales';
import {
  translateObject,
  TranslateParams,
} from '../../../store/translateAPI/translate/reducers/translate.slice';
import { showSuccess } from '../../../utils/Notifications';

// import styles from './ReportingDetailsThemesEditPage.module.css';

interface ReportingDetailsThemesEditPageProps {}

const ReportingDetailsThemesEditPage: FC<
  ReportingDetailsThemesEditPageProps
> = () => {
  const [theme, setTheme] = useState<ThemeResponseDto>();
  const [imageMeta, setImageMeta] = useState<ImageMeta>();

  const { id } = useParams();

  const navigate = useNavigate();

  const { status, error } = useSelector((state: RootState) => state.themeSlice);

  const { status: mediaStatus } = useSelector(
    (state: RootState) => state.mediaSlice
  );

  const { status: translateStatus } = useSelector(
    (state: RootState) => state.translateSlice
  );

  const dispatch = useDispatch<AppDispatch>();

  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const translate = async (toLanguage: LocalesLanguagesConstants) => {
    if (!theme) return;
    const params: TranslateParams<ThemeResponseDto> = {
      obj: theme,
      from: getLanguageKey(fromLanguage)!,
      to: [getLanguageKey(toLanguage)!],
    };

    const translationResponse = (await dispatch(
      translateObject(params)
    ).unwrap()) as ThemeResponseDto;

    const updateRequest: ThemeUpdateRequestDto = {
      name: translationResponse.name,
      description: translationResponse.description,
      icon: theme.icon,
      id: theme.id,
      published: theme.published,
    };

    await dispatch(
      updateTheme({
        updateRequest,
        language: toLanguage,
      })
    ).unwrap();
    setLanguage(toLanguage);
  };

  const generateAllTranslations = async () => {
    return await Promise.allSettled(
      Object.values(LocalesLanguagesConstants).map(translate)
    );
  };

  const generateTranslation = async () => {
    return await translate(toLanguage);
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchThemeById({ id: id, language: selectedLanguage }))
        .unwrap()
        .then((response: ThemeResponseDto) => setTheme(response));
    }
  }, [id, dispatch, selectedLanguage]);

  const handleOnChange = (theme: ThemeResponseDto) => {
    setTheme(theme);
  };

  const handleImageOnChange = (imageMeta: ImageMeta) => {
    setImageMeta(imageMeta);
  };

  const processMedia = async (
    file: string,
    meta: ImageMeta | undefined
  ): Promise<MediaSaveResponseDto> => {
    if (!meta) {
      return { href: file };
    }
    const formData = new FormData();
    formData.append(
      'file',
      new Blob([file], {
        type: meta.imageType ?? 'image/png',
      }),
      meta.imageSrc
    );
    const response = (await dispatch(
      saveMedia(formData)
    ).unwrap()) as MediaSaveResponseDto;
    return response;
  };

  const handleEditTheme = async (theme: ThemeResponseDto) => {
    const icon = await processMedia(theme.icon ?? '', imageMeta);
    const data: ThemeUpdateRequestDto = {
      id: theme.id,
      name: theme.name,
      description: theme.description,
      icon: icon.href,
      published: theme.published,
    };

    await dispatch(
      updateTheme({ updateRequest: data, language: selectedLanguage })
    );

    showSuccess('Collection saved successfully.');
    navigate('/reporting-details/themes');
  };

  if (
    status === 'loading' ||
    mediaStatus === 'loading' ||
    translateStatus === 'loading'
  ) {
    return <Spinner />;
  }

  if (status === 'failed') {
    return <div>{error}</div>;
  }

  return (
    <>
      {theme && (
        <ReportingDetailsThemesEditTemplate
          data={theme}
          handleEditTheme={handleEditTheme}
          handleOnChange={handleOnChange}
          handleImageOnChange={handleImageOnChange}
          setLanguage={setLanguage}
          selectedLanguage={selectedLanguage}
          fromLanguage={fromLanguage}
          setFromLanguage={setFromLanguage}
          toLanguage={toLanguage}
          setToLanguage={setToLanguage}
          generateTranslation={generateTranslation}
          generateAllTranslations={generateAllTranslations}
        />
      )}
      ;
    </>
  );
};

export default ReportingDetailsThemesEditPage;
